import translate from '@allesauto/i18n';
import { ChevronDown16, ChevronUp16 } from '@carbon/icons-react';
import clsx from 'clsx';
import React, {
  MouseEventHandler,
  ReactElement,
  ReactNode,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import Checkbox from '../checkbox';
import Price, { PriceProps } from '../price';
import RadioButton from '../radioButton';

export interface ICheckItemProps {
  selected: boolean;
  type?: 'checkbox' | 'radio';
  title?: string | ReactElement;
  priceProps?: PriceProps;
  priceLabel?: string;
  description?: string;
  descriptionMaxLength?: number;
  pricePosition?: 'before-child' | 'after-child';
  children?: ReactNode;
  onClick?: MouseEventHandler<HTMLDivElement>;
  className?: string;
  contentClassName?: string;
  additionalChildren?: ReactNode;
  additionalChildrenClassName?: string;
  preventLabelClick?: boolean;
  infoIconText?: string;
  trackingId?: string;
  disabled?: boolean;
}

const CheckItem: React.FC<ICheckItemProps> = (props) => {
  const {
    children,
    priceProps,
    priceLabel,
    title = '',
    selected = false,
    type,
    description,
    descriptionMaxLength,
    pricePosition = 'after-child',
    onClick,
    className: customClassName,
    contentClassName,
    additionalChildren,
    preventLabelClick = true,
    additionalChildrenClassName,
    infoIconText,
    trackingId,
    disabled,
    ...restProps
  } = props;

  const intl = useIntl();
  const [isExpanded, expand] = useState(false);

  const className = {
    wrapper: clsx(
      'bg-white border-1px border-solid checkitem-border-radius grid grid-cols-2 ring-[3px] rounded-default',
      selected
        ? 'border-white ring-purple80 monochrome:ring-black'
        : 'ring-white border-grey30',
      !disabled && 'group hover:border-white hover:ring-purple80',
      !disabled && 'monochrome:hover:ring-black md:cursor-pointer',
      'p-15px md:p-30px',
      customClassName
    ),
    title: 'col-span-2 md:col-auto',
    input: clsx('w-full', selected && 'font-weight-bold'),
    content: clsx(
      'col-span-2 font-regular leading-20px pt-5px text-p2 md:pt-15px',
      type && 'pl-30px',
      disabled ? 'text-grey60' : 'text-grey100',
      contentClassName
    ),
    price: '!text-p1',
    priceLabel:
      'text-p1 text-purple80 monochrome:text-black text-right font-weight-bold font-headline',
    topPriceLayer: 'sm:hidden md:block',
    bottomPriceLayer: '!text-headline4 mt-5px sm:block md:hidden',
    expandControls: clsx(
      'col-span-2 cursor-pointer decoration-solid pl-[3.125rem] text-purple80 monochrome:text-black',
      'sm:mt-10px sm:pr-15px sm:pt-15px sm:shadow-[inset_0_1px_0] sm:shadow-grey30 sm:text-p2 md:mt-15px md:shadow-none md:text-p1 md:underline'
    ),
    expandIcon: 'fill-purple80 monochrome:fill-black h-5 w-5',
  };

  const getPriceLabel = () => {
    if (priceProps) {
      return (
        <Price
          {...{
            priceTextKey: 'ui.PRICE.PRICE_ONLY',
            classNames: { mainPriceClass: className.price },
            ...priceProps,
          }}
        />
      );
    }

    if (priceLabel) {
      return <div className={className.priceLabel}>{priceLabel}</div>;
    }

    return null;
  };

  const isTextToLong = (text: string) =>
    descriptionMaxLength && text.length >= descriptionMaxLength;

  const getDescription = (text: string) => {
    if (!isExpanded && isTextToLong(text)) {
      return `${text.substring(0, descriptionMaxLength)}...`;
    }

    return text;
  };

  const expandTranslation = isExpanded
    ? 'ui.CHECKOUT.CHECK_ITEM.LESS'
    : 'ui.CHECKOUT.CHECK_ITEM.MORE';

  const priceLabelText = getPriceLabel();

  const getInfoIcon = infoIconText
    ? {
        text: infoIconText,
      }
    : undefined;

  return (
    <div
      className={className.wrapper}
      onClick={(event) => {
        !disabled && onClick && onClick(event);
      }}
      {...restProps}
    >
      <div className={className.title}>
        {type === 'checkbox' && (
          <Checkbox
            checked={selected}
            label={title}
            className={className.input}
            preventLabelClick={preventLabelClick}
            infoIcon={getInfoIcon}
            id={trackingId}
            disabled={disabled}
          />
        )}{' '}
        {type === 'radio' && (
          <RadioButton
            checked={selected}
            label={title}
            className={className.input}
            preventLabelClick={preventLabelClick}
            infoIcon={getInfoIcon}
            id={trackingId}
            disabled={disabled}
          />
        )}
        {!type && title && (
          <p className="font-weight-bold text-purple80 monochrome:text-grey90">
            {title}
          </p>
        )}
      </div>
      <div className={className.topPriceLayer}>{priceLabelText}</div>
      {(description || children || additionalChildren) && (
        <div className="col-span-2">
          <p
            className={className.content}
            dangerouslySetInnerHTML={{
              __html: description ? getDescription(description) : '',
            }}
          ></p>

          {additionalChildren && (
            <p className={additionalChildrenClassName}>{additionalChildren}</p>
          )}

          {pricePosition === 'before-child' && !!priceLabelText && (
            <div className={className.bottomPriceLayer}>{priceLabelText}</div>
          )}

          {children && children}

          {pricePosition === 'after-child' && !!priceLabelText && (
            <div className={className.bottomPriceLayer}>{priceLabelText}</div>
          )}
        </div>
      )}

      {description && isTextToLong(description) && (
        <div
          className={className.expandControls}
          onClick={(e) => {
            e.stopPropagation();
            expand(!isExpanded);
          }}
        >
          {translate(intl, expandTranslation)}

          <span className="float-right mr-[2px] sm:block md:hidden">
            {isExpanded ? (
              <ChevronUp16
                className={className.expandIcon}
                aria-hidden="true"
              />
            ) : (
              <ChevronDown16
                className={className.expandIcon}
                aria-hidden="true"
              />
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default CheckItem;
